import React, { useCallback, useEffect, useMemo } from 'react';
import { FCFormPageType, useForm } from 'new/elements/Form/useForm';
import { DatabaseSetup } from 'new/components/flows/types/elt/CreateELTFlow/DestinationSetupPage/DatabaseSetup';

import useELT from 'new/components/flows/types/elt/CreateELTFlow/ELTFlow/useELT';
import { useDispatch, useSelector } from 'react-redux';
import { probeGetConnectionTree } from 'js/actions';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import {
  mapToSinkConfig,
  parseSinkConfig,
} from 'new/components/flows/types/elt/CreateELTFlow/DestinationSetupPage/sinkConfigMapper';
import useResourceCreation from 'new/components/flows/types/elt/CreateELTFlow/DestinationSetupPage/useResourceCreation';
import { PROBE_GET_TREE_REQUEST } from 'js/constants/actionTypes';
import useInitialSetupPage from 'new/components/flows/types/elt/CreateELTFlow/DestinationSetupPage/useInitialSetupPage';
import { usePagesRef } from 'new/elements/Form/Form';
import { CenteredLoader } from 'new/elements/CenteredLoader';
import { Stack } from 'new/elements/Stack';
import {
  CONNECTION_CATEGORY_DATABASE,
  CONNECTION_CATEGORY_FILE,
} from 'js/constants/connectionCategories';
import { FileSystem } from 'new/components/flows/configure/FileSystem';
import { FORM_TYPE_CREATE_ELT } from 'js/constants/formTypes';
import flowTypes from '../../../flowTypes';
import useFlowsSetup from 'new/components/flows/controller/useFlowsSetup';
import usePaging from 'new/state/paging/repository/usePaging';

interface DestinationSetupPageProps {}

interface FileJsonSavePayload extends Record<string, unknown> {
  name?: string;
  description?: string;
}

const parseResource = (config, type) => {
  return mapToSinkConfig(config);
};

export const DestinationSetupPage: FCFormPageType<DestinationSetupPageProps> = (
  props: DestinationSetupPageProps,
) => {
  const {
    overrideNextFunction,
    setLoaderMessages,
    nextPage,
    setConfirmButtonTitle,
    setShowLeaveConfirmation,
  } = useForm();
  const pagesRef = usePagesRef();
  const {
    connectionType,
    selectedConnection,
    source,
    sink,
    selectedBucket,
    sinkConfig,
    allowCustomPrefixEditor,
    updateELTResourceConfig,
    updateFormData,
    credentialId,
    nexset,
    connectionCategory,
  } = useELT();

  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();

  const initialLoading = useInitialSetupPage();

  const { onSinkConfigChanged, onBucketSelected } = useFlowsSetup(
    FORM_TYPE_CREATE_ELT,
    flowTypes.elt,
  );

  const { getMetaInfoById } = usePaging();
  const meta = getMetaInfoById(selectedConnection);

  const editingId = (() => {
    if (connectionType === 'SINK' && !!sink?.id) return sink.id;
    return null;
  })();

  const saveResource = useResourceCreation(editingId);

  const getTrees = () => {
    if (!initialLoading && credentialId) {
      setLoaderMessages({
        [PROBE_GET_TREE_REQUEST]: 'Connecting to ' + meta?.name,
      });
      if (connectionCategory === CONNECTION_CATEGORY_DATABASE) {
        dispatch(probeGetConnectionTree(credentialId));
      }
      setLoaderMessages({});
    }
  };

  useEffect(() => {
    if (!sink && connectionType === 'SINK' && source) {
      setConfirmButtonTitle('Done');
      return;
    }
    if ((sink && connectionType === 'SINK') || (source && connectionType === 'SOURCE')) {
      setConfirmButtonTitle('Save');
    }
  }, [sink, source, connectionType]);

  useEffect(() => {
    getTrees();
  }, [initialLoading, credentialId]);

  useEffect(() => {
    overrideNextFunction(saveResource);

    // Cleanup: Reset override function when component unmounts or dependencies change
    return () => {
      overrideNextFunction(null);
    };
  }, [saveResource, overrideNextFunction]);

  const onConfigUpdated = (resourceConfig) => {
    updateELTResourceConfig({
      resourceConfig: parseResource(resourceConfig, connectionType),
    });
  };

  const onFileConfigUpdated = ({ name, description, ...fileConfig }: FileJsonSavePayload) => {
    /**
     * @Why?
     * SelectBucket submits flat FILE JSON; the database mapper expects nested config.
     */
    updateELTResourceConfig({
      resourceConfig: { name, description, sink_config: fileConfig },
    });
    /**
     * @Why?
     * File creation prefers the form name; update it before SelectBucket advances.
     */
    updateFormData({ sinkConfig: { ...sinkConfig, name } });
  };

  const onRequestProbeTree = useCallback(
    (node) => {
      dispatch(probeGetConnectionTree(credentialId, false, node));
    },
    [credentialId],
  );

  const initialResourceConfig = useMemo(() => {
    if (!sink && !source) return undefined;
    if (sink) return parseSinkConfig(sink);
  }, [sink, source]);

  const onDiscardAdvancedForm = () => {
    setShowLeaveConfirmation(true);
  };

  const onSubmitAdvancedForm = (resourceConfig) => {
    const newInfo = parseResource(resourceConfig, connectionType);
    updateELTResourceConfig({
      resourceConfig: newInfo,
    });
    setTimeout(() => {
      nextPage();
    }, 1);
  };

  if (initialLoading)
    return (
      <Stack padding="50px">
        <CenteredLoader />
      </Stack>
    );

  if (connectionCategory === CONNECTION_CATEGORY_FILE) {
    return (
      <div>
        <FileSystem
          formType={FORM_TYPE_CREATE_ELT}
          flowType={flowTypes.elt}
          sink={sink}
          source={source}
          nexset={nexset}
          sinkConfig={sinkConfig}
          shouldShowLoading={false}
          credentialId={credentialId}
          connectionType={connectionType}
          selectedBucket={selectedBucket}
          selectedConnection={selectedConnection}
          allowCustomPrefixEditor={allowCustomPrefixEditor}
          onBucketSelected={onBucketSelected}
          onSinkConfigChanged={onSinkConfigChanged}
          onSaveAdvancedMode={onFileConfigUpdated}
          nextFormStage={nextPage}
        />
      </div>
    );
  } else {
    return (
      <DatabaseSetup
        source={source}
        sink={sink}
        parentScrollRef={pagesRef}
        resourceConfig={initialResourceConfig}
        onConfigUpdated={onConfigUpdated}
        onRequestProbeTree={onRequestProbeTree}
        credentialId={credentialId}
        connectorType={selectedConnection}
        connectionCategory={connectionCategory}
        resourceType={connectionType}
        onDiscardAdvancedForm={onDiscardAdvancedForm}
        onSubmitAdvancedForm={onSubmitAdvancedForm}
      />
    );
  }
};

DestinationSetupPage.formConfig = {
  title: 'Configure',
};
